import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSocket } from '@/contexts/SocketContext';
import { messagingAPI } from '@/services/api';
import useConversationCall from './useConversationCall';
import CallSurface from './CallSurface';
import { useCallAlerts } from './useCallAlerts';
import '../messaging.css';

export const CallSessionContext = createContext(null);
export const useCallSession = () => useContext(CallSessionContext);

export function CallNotice() {
  const session = useCallSession();
  if (!session?.callState.error || session.callState.call) return null;
  return <section role="status" className="call-notice"><p>{session.callState.error}</p><button onClick={session.callState.dismissError} aria-label="Dismiss call error">Dismiss</button></section>;
}

/** One authenticated call owner across client-side routes. Never page-scoped. */
export function CallSessionProvider({ children, enabled = true, token, role }) {
  const { socket, connected, connectSocket, disconnectSocket, sendCallEvent } = useSocket();
  const [conversation, setConversation] = useState(null);
  const [incomingConversation, setIncomingConversation] = useState(null);
  const [callPeer, setCallPeer] = useState(null);
  const incomingIdRef = useRef(null);
  const aliveRef = useRef(true);
  useEffect(() => { aliveRef.current = true; return () => { aliveRef.current = false; incomingIdRef.current = null; }; }, []);
  const navigate = useNavigate();
  const location = useLocation();
  const onIncomingCall = useCallback((payload) => {
    incomingIdRef.current = payload.call_id;
    const row = { conversation_id: payload.conversation_id, other_party: { name: 'Participant' } };
    setIncomingConversation(row);
    setCallPeer(row);
    messagingAPI.listConversations(1, 100).then((result) => {
      const match = result.conversations?.find((item) => item.conversation_id === payload.conversation_id);
      if (aliveRef.current && match && incomingIdRef.current === payload.call_id) { setIncomingConversation(match); setCallPeer(match); }
    }).catch(() => {});
  }, []);
  const callState = useConversationCall({ socket: enabled ? socket : null, connected: enabled && connected, conversation, sendCallEvent, onIncomingCall });
  const { call, endCall, restoreInvitation } = callState;
  useEffect(() => {
    if (!enabled && call) endCall();
  }, [enabled, call, endCall]);
  const selectConversation = useCallback((row) => {
    setConversation(row);
    if (!callState.call || callState.isTerminal) setCallPeer(row);
  }, [callState.call, callState.isTerminal]);
  useEffect(() => {
    if (!enabled) { disconnectSocket?.(); return undefined; }
    const authToken = token || localStorage.getItem('proovam_token');
    if (!authToken) return undefined;
    return connectSocket(authToken);
  }, [enabled, token, connectSocket, disconnectSocket]);
  const openChat = useCallback(() => {
    const id = callState.call?.conversation_id || incomingConversation?.conversation_id;
    const actorRole = role || localStorage.getItem('proovam_user_type') || 'client';
    navigate(`/${actorRole}/messages${id ? `?conversation_id=${encodeURIComponent(id)}` : ''}`);
  }, [navigate, role, callState.call?.conversation_id, incomingConversation?.conversation_id]);
  useEffect(() => {
    if (!enabled) return undefined;
    let cancelled = false;
    const restore = async (callId) => {
      if (typeof callId !== 'string' || callId.length > 128) return;
      try {
        const result = await messagingAPI.getCall(callId);
        if (cancelled || !result.call) return;
        restoreInvitation(result.call);
        navigate(`/${role || 'client'}/messages?conversation_id=${encodeURIComponent(result.call.conversation_id)}`, { replace: true });
      } catch { /* Expired notification or unauthorised account; no call is fabricated. */ }
    };
    const message = (event) => { if (event.data?.type === 'CALL_NOTIFICATION') restore(event.data.call_id); };
    navigator.serviceWorker?.addEventListener?.('message', message);
    const callId = new URLSearchParams(location.search).get('call_id');
    if (callId) restore(callId);
    return () => { cancelled = true; navigator.serviceWorker?.removeEventListener?.('message', message); };
  }, [enabled, role, navigate, location.search, restoreInvitation]);
  const alerts = useCallAlerts(callState, callPeer?.other_party?.name || 'Participant', openChat, enabled);
  const value = useMemo(() => ({ callState, selectConversation, incomingConversation, alerts }), [callState, selectConversation, incomingConversation, alerts]);
  return <CallSessionContext.Provider value={value}>
    {children}
    {enabled && <CallSurface callState={callState} otherPartyName={(callState.call?.conversation_id === conversation?.conversation_id ? conversation : callPeer)?.other_party?.name || 'Participant'} onOpenChat={openChat} />}
    {enabled && !location.pathname.endsWith('/messages') && callState.call?.status === 'ringing' && <span className="sr-only" role="status">Incoming calls are available across app pages.</span>}
  </CallSessionContext.Provider>;
}
